import { Bot, FolderKanban, Lock, Plus, Star, Tag as TagIcon, X } from 'lucide-react';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { ApiError } from '../../api/errors';
import { useUserMap } from '../../api/org';
import { canEditProject, useCreateTag, useProjectDetailsMany, useProjects, useTags } from '../../api/projects';
import { collaboratorsOf, observersOf, type ParticipantRole, useAddTaskToProject, useFollower, useRemoveTaskFromProject, useTaskTag, useUpdateTask } from '../../api/tasks';
import type { ProjectDetails, ProjectField, Task, UpdateTaskInput } from '../../api/types';
import { Avatar } from '../../components/ui/Avatar';
import { Badge } from '../../components/ui/Badge';
import { Button, IconButton } from '../../components/ui/Button';
import { type Option, SearchSelect } from '../../components/ui/Combobox';
import { DatePanel } from '../../components/ui/DatePicker';
import { Input, Select, Switch } from '../../components/ui/Field';
import { Popover } from '../../components/ui/Popover';
import { useToast } from '../../components/ui/Toast';
import { Tip } from '../../components/ui/Tooltip';
import { cn } from '../../lib/cn';
import { TaskExtFieldRows } from '../../app/TaskExtensions';
import { DependenciesRow } from './TaskRelations';
import { useGuardedMove } from './TransitionGuard';
import { TermHint } from '../knowledge/TermHint';
import { customFieldKey } from '../knowledge/fieldTerms';
import { formatDate, formatDateFull, formatDateTime, workingHoursSince } from '../../lib/dates';
import { countLabel, formatMinutes, formatNumber } from '../../lib/format';
import { AssigneeControl, DueDateControl, useUserOptions } from './TaskControls';
import { PRIORITY_LABELS, SOURCE_LABELS } from './taskUtils';

/** Поле в сітці реквізитів: без рамки до наведення чи фокуса, текст вирівняно з іншими значеннями. */
const GHOST_CONTROL = 'h-8 -ml-2.5 border-transparent bg-transparent hover:border-line-input focus:bg-surface';
/** Для списку, що розкривається, зсув — на обгортці, інакше ширина поля зменшується й текст обрізається. */
const GHOST_SELECT = 'h-8 border-transparent bg-transparent hover:border-line-input focus:bg-surface';

export function FieldRow({ label, children, hint }: { label: ReactNode; children: ReactNode; hint?: ReactNode }) {
  return (
    <div className="grid grid-cols-[112px_minmax(0,1fr)] items-start gap-x-3 sm:grid-cols-[136px_minmax(0,1fr)]">
      <dt className="flex min-h-8 items-center text-meta text-fg-3">{label}</dt>
      <dd className="min-h-8 min-w-0">
        <div className="flex min-h-8 flex-wrap items-center gap-1.5">{children}</div>
        {hint && <p className="mt-0.5 text-caption text-fg-3">{hint}</p>}
      </dd>
    </div>
  );
}

function useSaveTask(task: Task) {
  const update = useUpdateTask();
  const toast = useToast();
  return (body: UpdateTaskInput, ok?: string) =>
    update.mutate(
      { task, body },
      {
        onSuccess: () => ok && toast.success(ok),
        onError: (e) => {
          if (e instanceof ApiError) {
            const extra = e.errors.map((x) => x.message).filter((m) => m !== e.detail);
            toast.error(e.status === 412 ? 'Задачу щойно змінив інший працівник' : 'Зміни не збережено', [e.detail, ...extra].join(' '));
          } else toast.error('Зміни не збережено');
        },
      },
    );
}

/** Дата без причини (крайній строк, дата початку) — у спливному вікні. */
function PlainDateControl({ task, field, label, disabled }: { task: Task; field: 'deadlineOn' | 'startOn'; label: string; disabled?: boolean }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const save = useSaveTask(task);
  const value = task[field];
  return (
    <>
      <button
        type="button"
        disabled={disabled}
        aria-label={`${label}: ${value ? formatDateFull(value) : "не вказано"}, змінити`}
        onClick={(e) => setAnchor(e.currentTarget)}
        className={cn('tnum -ml-1.5 inline-flex min-h-8 items-center rounded-sm px-1.5 text-body hover:bg-surface-2 disabled:hover:bg-transparent', !value && 'text-fg-3')}
      >
        {value ? formatDate(value) : 'Не вказано'}
      </button>
      <Popover open={!!anchor} onClose={() => setAnchor(null)} anchor={anchor} aria-label={label}>
        <DatePanel
          value={value}
          allowClear
          clearLabel="Прибрати дату"
          onSelect={(d) => {
            setAnchor(null);
            save({ [field]: d } as UpdateTaskInput);
          }}
        />
      </Popover>
    </>
  );
}

/** Етапи процесу як доріжка: поточний етап позначено, клік — перемістити задачу. */
export function StageTrack({ task, project, disabled }: { task: Task; project: ProjectDetails; disabled?: boolean }) {
  const membership = task.memberships.find((m) => m.projectId === project.id);
  const guardedMove = useGuardedMove();
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  if (!membership || project.sections.length < 2) return null;
  const currentIdx = project.sections.findIndex((s) => s.id === membership.sectionId);
  const current = project.sections[currentIdx];
  const hours = current?.slaHours ? workingHoursSince(membership.enteredSectionAt) : 0;
  const overSla = !!current?.slaHours && hours > current.slaHours;
  return (
    <div>
      <ol className="flex flex-wrap gap-1" aria-label={`Етапи процесу «${project.name}»`}>
        {project.sections.map((s, i) => {
          const isCur = s.id === membership.sectionId;
          const passed = currentIdx >= 0 && i < currentIdx;
          return (
            <li key={s.id} className="shrink-0">
              <button
                type="button"
                disabled={disabled || isCur || busy}
                aria-current={isCur ? 'step' : undefined}
                onClick={async () => {
                  setBusy(true);
                  const r = await guardedMove({ task, body: { projectId: project.id, sectionId: s.id }, sectionName: s.name });
                  setBusy(false);
                  if (r.outcome === 'moved') toast.success(`Етап: ${s.name}`);
                  else if (r.outcome === 'failed') {
                    const e = r.error;
                    toast.error('Задачу не переміщено', e instanceof ApiError ? [e.detail, ...e.errors.map((x) => x.message)].join(' ') : undefined);
                  }
                }}
                className={cn(
                  'flex h-7 items-center gap-1.5 rounded-sm border px-2 text-caption whitespace-nowrap transition-colors',
                  isCur ? 'border-accent bg-accent-soft font-semibold text-accent-fg' : passed ? 'border-line bg-surface-2 text-fg-2' : 'border-line text-fg-3',
                  !isCur && !disabled && 'hover:border-line-input hover:text-fg',
                )}
              >
                {isCur && <span className="size-1.5 rounded-full bg-accent" aria-hidden />}
                {s.name}
              </button>
            </li>
          );
        })}
      </ol>
      {current && (
        <p className="mt-1 text-caption text-fg-3">
          На етапі з {formatDateTime(membership.enteredSectionAt)}
          {current.slaHours ? (
            <span className={cn(overSla && 'font-medium text-warning-fg')}>
              {' '}
              · норматив {current.slaHours}&nbsp;год{overSla ? `, минуло ≈${Math.round(hours)}\u00a0роб. год` : ''}
            </span>
          ) : null}
        </p>
      )}
    </div>
  );
}

function ProjectsRow({ task, details, disabled }: { task: Task; details: Map<string, ProjectDetails>; disabled?: boolean }) {
  const projects = useProjects();
  const add = useAddTaskToProject();
  const remove = useRemoveTaskFromProject();
  const guardedMove = useGuardedMove();
  const toast = useToast();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [secAnchor, setSecAnchor] = useState<{ el: HTMLElement; projectId: string } | null>(null);
  const options: Option[] = (projects.data ?? [])
    .filter((p) => !p.archivedAt && canEditProject(p.myRole) && !task.memberships.some((m) => m.projectId === p.id))
    .map((p) => ({ value: p.id, label: p.name, icon: <FolderKanban className="size-4 text-fg-3" aria-hidden /> }));
  const err = (title: string) => (e: unknown) => toast.error(title, e instanceof ApiError ? [e.detail, ...e.errors.map((x) => x.message)].join(' ') : undefined);
  const secProject = secAnchor ? details.get(secAnchor.projectId) : undefined;

  return (
    <>
      {task.memberships.map((m) => {
        const p = details.get(m.projectId);
        const sec = p?.sections.find((s) => s.id === m.sectionId);
        return (
          <span key={m.projectId} className="inline-flex max-w-full flex-col items-stretch rounded-sm border border-line bg-surface text-meta @md:flex-row">
            <Link to={`/projects/${m.projectId}`} className="min-w-0 py-1 pr-2 pl-2 font-medium break-words text-fg hover:underline @md:pr-1">
              {p?.name ?? 'Проєкт'}
            </Link>
            <button
              type="button"
              disabled={disabled}
              onClick={(e) => setSecAnchor({ el: e.currentTarget, projectId: m.projectId })}
              aria-label={`Етап у проєкті ${p?.name ?? ''}: ${sec?.name ?? 'без етапу'}. Перемістити`}
              className="min-w-0 border-t border-line px-2 py-1 text-left break-words text-fg-2 hover:bg-surface-2 @md:border-t-0 @md:border-l"
            >
              {sec?.name ?? 'Без етапу'}
            </button>
            {task.memberships.length > 1 && !disabled && (
              <button
                type="button"
                aria-label={`Вилучити з проєкту ${p?.name ?? ''}`}
                onClick={() => remove.mutate({ id: task.id, projectId: m.projectId }, { onError: err('Задачу не вилучено з проєкту') })}
                className="inline-flex min-h-6 min-w-6 items-center justify-center border-t border-line text-fg-3 hover:bg-surface-2 hover:text-fg @md:border-t-0 @md:border-l"
              >
                <X className="size-3" aria-hidden />
              </button>
            )}
          </span>
        );
      })}
      {!disabled && (
        <Tip content="Додати до ще одного проєкту">
          <IconButton label="Додати до проєкту" size="sm" onClick={(e) => setAnchor(e.currentTarget)}>
            <Plus className="size-4" aria-hidden />
          </IconButton>
        </Tip>
      )}
      <SearchSelect
        open={!!anchor}
        onClose={() => setAnchor(null)}
        anchor={anchor}
        options={options}
        value={null}
        label="Додати до проєкту"
        placeholder="Пошук проєкту"
        emptyText="Немає доступних проєктів"
        onSelect={(projectId) => add.mutate({ id: task.id, projectId }, { onSuccess: () => toast.success('Задачу додано до проєкту'), onError: err('Задачу не додано до проєкту') })}
      />
      <SearchSelect
        open={!!secAnchor}
        onClose={() => setSecAnchor(null)}
        anchor={secAnchor?.el ?? null}
        options={(secProject?.sections ?? []).map((s) => ({
          value: s.id,
          label: s.name,
          description: [s.slaHours ? `норматив ${s.slaHours}\u00a0год` : null, s.wipLimit ? `ліміт ${s.wipLimit}` : null].filter(Boolean).join(', ') || undefined,
        }))}
        value={task.memberships.find((m) => m.projectId === secAnchor?.projectId)?.sectionId ?? null}
        label="Етап"
        placeholder="Пошук етапу"
        onSelect={async (sectionId) => {
          if (!secAnchor) return;
          const name = secProject?.sections.find((x) => x.id === sectionId)?.name;
          const r = await guardedMove({ task, body: { projectId: secAnchor.projectId, sectionId }, sectionName: name });
          if (r.outcome === 'moved') toast.success(name ? `Етап: ${name}` : 'Задачу переміщено');
          else if (r.outcome === 'failed') err('Задачу не переміщено')(r.error);
        }}
      />
    </>
  );
}

function TagsRow({ task, disabled }: { task: Task; disabled?: boolean }) {
  const tags = useTags();
  const setTag = useTaskTag();
  const createTag = useCreateTag();
  const toast = useToast();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [query, setQuery] = useState('');
  const byId = new Map((tags.data ?? []).map((t) => [t.id, t]));
  const onErr = (e: unknown) => toast.error('Тег не змінено', e instanceof ApiError ? e.detail : undefined);
  const exists = (tags.data ?? []).some((t) => t.name.toLocaleLowerCase('uk') === query.trim().toLocaleLowerCase('uk'));
  return (
    <>
      {task.tagIds.map((id) => (
        <span key={id} className="inline-flex h-6 items-center gap-1 rounded-xs border border-line bg-surface-2 pr-0.5 pl-1.5 text-caption text-fg-2">
          <TagIcon className="size-3" aria-hidden />
          {byId.get(id)?.name ?? 'тег'}
          {!disabled && (
            <button
              type="button"
              aria-label={`Прибрати тег ${byId.get(id)?.name ?? ''}`}
              onClick={() => setTag.mutate({ id: task.id, tagId: id, add: false }, { onError: onErr })}
              className="inline-flex size-4 items-center justify-center rounded-xs text-fg-3 hover:bg-surface-3 hover:text-fg"
            >
              <X className="size-3" aria-hidden />
            </button>
          )}
        </span>
      ))}
      {!task.tagIds.length && <span className="text-meta text-fg-3">Немає</span>}
      {!disabled && (
        <IconButton label="Додати тег" size="sm" onClick={(e) => setAnchor(e.currentTarget)}>
          <Plus className="size-4" aria-hidden />
        </IconButton>
      )}
      <SearchSelect
        open={!!anchor}
        onClose={() => {
          setAnchor(null);
          setQuery('');
        }}
        anchor={anchor}
        options={(tags.data ?? []).map((t) => ({ value: t.id, label: t.name }))}
        value={task.tagIds}
        multiple
        label="Теги"
        placeholder="Пошук або новий тег"
        onQueryChange={setQuery}
        onSelect={(tagId) => setTag.mutate({ id: task.id, tagId, add: !task.tagIds.includes(tagId) }, { onError: onErr })}
        footer={
          query.trim() && !exists ? (
            <button
              type="button"
              onClick={() =>
                createTag.mutate(
                  { name: query.trim() },
                  { onSuccess: (t) => setTag.mutate({ id: task.id, tagId: t.id, add: true }, { onError: onErr }), onError: onErr },
                )
              }
              className="flex w-full items-center gap-2 rounded-sm px-2.5 py-1.5 text-left text-meta hover:bg-surface-2"
            >
              <Plus className="size-3.5" aria-hidden />
              Створити тег «{query.trim()}»
            </button>
          ) : undefined
        }
      />
    </>
  );
}

/**
 * Люди в задачі з ролями: «Учасники» працюють у задачі (змінюють поля, підзадачі, коментарі, облік часу), але не відповідальні;
 * «Спостерігачі» бачать задачу, коментують і отримують сповіщення. Виконавець — окремо.
 */
function PeopleRow({ task, role, disabled }: { task: Task; role: ParticipantRole; disabled?: boolean }) {
  const users = useUserMap();
  const options = useUserOptions({ excludeIds: task.assigneeId ? [task.assigneeId] : [] });
  const follower = useFollower();
  const toast = useToast();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const ids = role === 'collaborator' ? collaboratorsOf(task) : observersOf(task);
  const other = role === 'collaborator' ? observersOf(task) : collaboratorsOf(task);
  const label = role === 'collaborator' ? 'Учасники' : 'Спостерігачі';
  const onErr = (e: unknown) => toast.error(`${label}: зміну не збережено`, e instanceof ApiError ? e.detail : undefined);
  return (
    <>
      {ids.map((id) => {
        const u = users.get(id);
        return (
          <span key={id} className="inline-flex h-7 max-w-full items-center gap-1.5 rounded-full border border-line bg-surface pr-1 pl-0.5 text-meta">
            <Avatar name={u?.name} src={u?.avatarUrl} size={22} />
            <span className="truncate">{u?.name ?? 'Працівник'}</span>
            {!disabled && (
              <button
                type="button"
                aria-label={`Вилучити: ${u?.name ?? ''}`}
                onClick={() => follower.mutate({ id: task.id, userId: id, add: false }, { onError: onErr })}
                className="inline-flex size-5 items-center justify-center rounded-full text-fg-3 hover:bg-surface-2 hover:text-fg"
              >
                <X className="size-3" aria-hidden />
              </button>
            )}
          </span>
        );
      })}
      {!ids.length && <span className="text-body text-fg-3">Немає</span>}
      {!disabled && (
        <IconButton label={role === 'collaborator' ? 'Додати учасника' : 'Додати спостерігача'} size="sm" onClick={(e) => setAnchor(e.currentTarget)}>
          <Plus className="size-4" aria-hidden />
        </IconButton>
      )}
      <SearchSelect
        open={!!anchor}
        onClose={() => setAnchor(null)}
        anchor={anchor}
        options={options}
        value={ids}
        multiple
        label={label}
        placeholder="Пошук працівника"
        header={
          <p className="border-b border-line px-3 py-2 text-caption text-fg-3">
            {role === 'collaborator'
              ? 'Учасник працює в задачі (поля, підзадачі, коментарі, облік часу), але не є відповідальним — напр. КМ у «Відпрацюванні».'
              : 'Спостерігач бачить задачу, коментує й отримує сповіщення, але не змінює її.'}
          </p>
        }
        onSelect={(userId) => {
          const has = ids.includes(userId);
          // Вибір людини з іншої ролі — зміна ролі тим самим викликом.
          follower.mutate({ id: task.id, userId, add: !has, role: has ? undefined : role }, { onError: onErr });
          if (!has && other.includes(userId)) toast.info(`${users.get(userId)?.name ?? 'Працівника'} переведено в «${label.toLowerCase()}»`);
        }}
      />
    </>
  );
}

/** Значення користувацького поля з редагуванням за типом. */
function CustomFieldValue({ task, field, disabled }: { task: Task; field: ProjectField; disabled?: boolean }) {
  const users = useUserMap();
  const userOptions = useUserOptions();
  const save = useSaveTask(task);
  const raw = task.customFields[field.id];
  const [draft, setDraft] = useState<string>(raw == null ? '' : String(raw));
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  useEffect(() => setDraft(raw == null ? '' : Array.isArray(raw) ? '' : String(raw)), [raw]);
  const commit = (value: unknown) => {
    const same = JSON.stringify(value ?? null) === JSON.stringify(raw ?? null);
    if (!same) save({ customFields: { [field.id]: value } });
  };
  const textLike = ['text', 'url', 'email', 'phone'].includes(field.type);
  const numberLike = ['number', 'currency', 'percent', 'duration'].includes(field.type);

  if (textLike || numberLike) {
    const display =
      raw == null
        ? ''
        : field.type === 'currency'
          ? `${formatNumber(Number(raw), { minimumFractionDigits: field.precision ?? 2, maximumFractionDigits: field.precision ?? 2 })} ${field.currency ?? ''}`
          : field.type === 'percent'
            ? `${formatNumber(Number(raw))}\u00a0%`
            : field.type === 'duration'
              ? formatMinutes(Number(raw))
              : String(raw);
    return (
      <span className="flex w-full min-w-0 items-center gap-2">
        <Input
          aria-label={field.name}
          disabled={disabled}
          type={field.type === 'email' ? 'email' : field.type === 'url' ? 'url' : 'text'}
          inputMode={numberLike ? 'decimal' : field.type === 'phone' ? 'tel' : undefined}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => {
            const v = draft.trim();
            if (!v) return commit(null);
            if (numberLike) {
              const n = Number(v.replace(',', '.').replace(/\s/g, ''));
              if (Number.isFinite(n)) commit(n);
              else setDraft(raw == null ? '' : String(raw));
            } else commit(v);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
          }}
          placeholder="Не вказано"
          className={GHOST_CONTROL}
        />
        {numberLike && raw != null && <span className="tnum shrink-0 text-meta text-fg-3">{display}</span>}
        {field.type === 'url' && typeof raw === 'string' && (
          <a href={raw} target="_blank" rel="noopener noreferrer" className="shrink-0 text-meta text-accent-fg underline underline-offset-2">
            Відкрити
          </a>
        )}
      </span>
    );
  }
  if (field.type === 'boolean')
    return <Switch checked={raw === true} onChange={(v) => commit(v)} label={<span className="sr-only">{field.name}</span>} disabled={disabled} />;
  if (field.type === 'rating')
    return (
      <span className="flex items-center gap-0.5" role="radiogroup" aria-label={field.name}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={raw === n}
            aria-label={`${n} з 5`}
            disabled={disabled}
            onClick={() => commit(raw === n ? null : n)}
            className="inline-flex size-7 items-center justify-center rounded-sm hover:bg-surface-2"
          >
            <Star className={cn('size-4', typeof raw === 'number' && n <= raw ? 'fill-current text-warning' : 'text-fg-3')} aria-hidden />
          </button>
        ))}
      </span>
    );
  if (field.type === 'enum')
    return (
      <Select aria-label={field.name} disabled={disabled} value={typeof raw === 'string' ? raw : ''} onChange={(e) => commit(e.target.value || null)} className={cn(GHOST_SELECT, 'max-w-64')} wrapperClassName="-ml-2.5">
        <option value="">Не вказано</option>
        {field.options
          .filter((o) => !o.archived || o.id === raw)
          .map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
      </Select>
    );
  if (field.type === 'date')
    return (
      <>
        <button type="button" disabled={disabled} onClick={(e) => setAnchor(e.currentTarget)} className={cn('tnum -ml-1.5 min-h-8 rounded-sm px-1.5 text-body hover:bg-surface-2', raw == null && 'text-fg-3')}>
          {typeof raw === 'string' ? formatDate(raw) : 'Не вказано'}
        </button>
        <Popover open={!!anchor} onClose={() => setAnchor(null)} anchor={anchor} aria-label={field.name}>
          <DatePanel value={typeof raw === 'string' ? raw : null} allowClear clearLabel="Прибрати дату" onSelect={(d) => { setAnchor(null); commit(d); }} />
        </Popover>
      </>
    );
  if (field.type === 'datetime')
    return (
      <Input
        aria-label={field.name}
        type="datetime-local"
        disabled={disabled}
        defaultValue={typeof raw === 'string' ? raw.slice(0, 16) : ''}
        onBlur={(e) => commit(e.target.value ? new Date(e.target.value).toISOString() : null)}
        className={cn(GHOST_CONTROL, 'max-w-64')}
      />
    );
  const multi = field.type === 'multi_enum' || field.type === 'people';
  if (multi) {
    const values = Array.isArray(raw) ? (raw as string[]) : [];
    const opts: Option[] = field.type === 'people' ? userOptions : field.options.filter((o) => !o.archived).map((o) => ({ value: o.id, label: o.name }));
    const label = (v: string) => (field.type === 'people' ? (users.get(v)?.name ?? 'Працівник') : (field.options.find((o) => o.id === v)?.name ?? v));
    return (
      <>
        {values.length ? values.map((v) => <Badge key={v}>{label(v)}</Badge>) : <span className="text-meta text-fg-3">Не вказано</span>}
        <Button size="sm" variant="ghost" disabled={disabled} onClick={(e) => setAnchor(e.currentTarget)}>
          Змінити
        </Button>
        <SearchSelect
          open={!!anchor}
          onClose={() => setAnchor(null)}
          anchor={anchor}
          options={opts}
          value={values}
          multiple
          label={field.name}
          onSelect={(v) => commit(values.includes(v) ? values.filter((x) => x !== v) : [...values, v])}
        />
      </>
    );
  }
  return <span className="text-meta text-fg-3">{raw == null ? 'Не вказано' : String(raw)}</span>;
}

/**
 * Поля проєктів задачі за призначенням: фінансові (чутливі — лише з finance:read, сервер іншим їх не віддає),
 * обов'язкові на поточному етапі (без них задачу не перемістити далі) і решта.
 */
export function useTaskProjectFields(task: Task) {
  const projectIds = useMemo(() => task.memberships.map((m) => m.projectId), [task.memberships]);
  const details = useProjectDetailsMany(projectIds);
  return useMemo(() => {
    const seen = new Map<string, ProjectField>();
    for (const pid of projectIds) for (const f of details.get(pid)?.customFields ?? []) if (!seen.has(f.id) && !f.archivedAt) seen.set(f.id, f);
    const requiredHere = new Set<string>();
    for (const m of task.memberships) {
      const sec = details.get(m.projectId)?.sections.find((x) => x.id === m.sectionId);
      for (const id of sec?.requiredFieldIds ?? []) requiredHere.add(id);
    }
    const all = [...seen.values()];
    const isEmpty = (f: ProjectField) => task.customFields[f.id] === undefined || task.customFields[f.id] === null || task.customFields[f.id] === '';
    const finance = all.filter((f) => f.sensitive);
    const work = all.filter((f) => !f.sensitive);
    return {
      details,
      requiredHere,
      finance,
      /** Поля, без яких задача не пройде поточний етап, — на видноті серед головних реквізитів. */
      workRequired: work.filter((f) => requiredHere.has(f.id)),
      workOther: work.filter((f) => !requiredHere.has(f.id)),
      /** Скільки фінансових полів, обов'язкових на поточному етапі, ще не заповнено. */
      financeMissing: finance.filter((f) => requiredHere.has(f.id) && isEmpty(f)).length,
    };
  }, [details, projectIds, task.memberships, task.customFields]);
}

export type TaskFieldsPart = 'primary' | 'secondary' | 'all' | 'finance';

/**
 * Реквізити задачі. `part`: «primary» — головне для виконання (виконавець, строки, проєкт і етап, обов'язкові поля етапу);
 * «secondary» — решта (залежності, час, пріоритет, теги, учасники, поля проєкту, хто створив); «all» — усе разом
 * (бічна колонка сторінки задачі); «finance» — фінансові поля (вкладка «Фінанси»).
 */
export function TaskFieldsGrid({ task, canEdit, part = 'all' }: { task: Task; canEdit: boolean; part?: TaskFieldsPart }) {
  const save = useSaveTask(task);
  const users = useUserMap();
  const { details, requiredHere, finance, workRequired, workOther } = useTaskProjectFields(task);
  const creator = task.createdBy ? users.get(task.createdBy) : undefined;
  const disabled = !canEdit || !!task.archivedAt;
  const primary = part === 'primary' || part === 'all';
  const secondary = part === 'secondary' || part === 'all';

  const deadlineRow = (
    <FieldRow label="Крайній строк" hint={task.deadlineOn ? 'Для клієнта чи органу' : undefined}>
      <PlainDateControl task={task} field="deadlineOn" label="Крайній строк" disabled={disabled} />
    </FieldRow>
  );
  const customRow = (f: ProjectField) => (
    <FieldRow
      key={f.id}
      label={
        <span className="flex min-w-0 items-center gap-1 py-1">
          <span className="min-w-0 break-words">{f.name}</span>
          <TermHint fieldKey={customFieldKey(f.name)} label={f.name} />
          {f.sensitive && (
            <>
              <Tip content="Фінансове поле: бачать лише ролі з доступом до фінансів">
                <Lock className="size-3 shrink-0 text-fg-3" aria-hidden />
              </Tip>
              <span className="sr-only">(фінансове поле)</span>
            </>
          )}
        </span>
      }
      hint={f.required || requiredHere.has(f.id) ? (requiredHere.has(f.id) ? 'Обов’язкове на поточному етапі' : 'Обов’язкове в проєкті') : f.description ?? undefined}
    >
      <CustomFieldValue task={task} field={f} disabled={disabled || (f.adminOnly && !canEdit)} />
    </FieldRow>
  );

  if (part === 'finance') return <dl className="@container flex flex-col gap-1">{finance.map(customRow)}</dl>;

  return (
    <dl className="@container flex flex-col gap-1">
      {primary && (
        <>
          <FieldRow label="Виконавець">
            <AssigneeControl task={task} variant="field" disabled={disabled || task.status === 'completed'} placement="bottom-start" />
          </FieldRow>
          <FieldRow
            label="Строк"
            hint={
              task.dueChangeCount > 0 && task.baselineDueOn
                ? `Базовий строк: ${formatDate(task.baselineDueOn)} · переносився ${countLabel(task.dueChangeCount, { one: 'раз', few: 'рази', many: 'разів' })}`
                : undefined
            }
          >
            <DueDateControl task={task} variant="field" disabled={disabled || task.status === 'completed'} placement="bottom-start" />
          </FieldRow>
          {/* Заданий крайній строк — зобов'язання перед клієнтом чи органом: він серед головного. */}
          {(task.deadlineOn || part === 'all') && deadlineRow}
          <FieldRow label="Проєкти й етапи">
            <ProjectsRow task={task} details={details} disabled={disabled} />
          </FieldRow>
          {workRequired.map(customRow)}
        </>
      )}
      {secondary && (
        <>
          {part === 'secondary' && !task.deadlineOn && deadlineRow}
          <FieldRow label="Залежності" hint={task.dependsOnIds.length ? 'Задачу не завершити, доки ці не виконано' : undefined}>
            <DependenciesRow task={task} disabled={disabled} />
          </FieldRow>
          <TaskExtFieldRows task={task} />
          <FieldRow label="Пріоритет">
            <Select aria-label="Пріоритет" disabled={disabled} value={task.priority} onChange={(e) => save({ priority: e.target.value as Task['priority'] })} className={cn(GHOST_SELECT, 'max-w-48')} wrapperClassName="-ml-2.5">
              {(Object.keys(PRIORITY_LABELS) as Task['priority'][]).map((p) => (
                <option key={p} value={p}>
                  {PRIORITY_LABELS[p]}
                </option>
              ))}
            </Select>
            <label className="ml-2 inline-flex items-center gap-2 text-meta">
              <input
                type="checkbox"
                disabled={disabled}
                checked={task.urgent}
                onChange={(e) => save({ urgent: e.target.checked }, e.target.checked ? `${task.key}: позначено «Терміново»` : undefined)}
                className="size-4 accent-[var(--c-accent)]"
              />
              Терміново
            </label>
          </FieldRow>
          <FieldRow label="Дата початку">
            <PlainDateControl task={task} field="startOn" label="Дата початку" disabled={disabled} />
          </FieldRow>
          <FieldRow label="Теги">
            <TagsRow task={task} disabled={disabled} />
          </FieldRow>
          <FieldRow label="Учасники" hint="Працюють у задачі, але не відповідальні">
            <PeopleRow task={task} role="collaborator" disabled={disabled} />
          </FieldRow>
          <FieldRow label="Спостерігачі">
            <PeopleRow task={task} role="observer" disabled={!!task.archivedAt} />
          </FieldRow>
          {workOther.map(customRow)}
          <FieldRow label="Створено">
            <span className="flex flex-wrap items-center gap-2 text-meta text-fg-2">
              {creator && <Avatar name={creator.name} size={20} />}
              <span>
                {creator?.name ?? 'ЄРС'} · {formatDateTime(task.createdAt)}
              </span>
              <Badge>{SOURCE_LABELS[task.source]}</Badge>
              {task.createdByAgent && (
                <Badge tone="info" icon={<Bot className="size-3" aria-hidden />}>
                  Створено агентом {task.createdByAgent}
                </Badge>
              )}
            </span>
          </FieldRow>
        </>
      )}
    </dl>
  );
}
